import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthImage } from '@/shared/hooks'
import { IconImage, IconX, IconSpinner } from '@/app/components/icons'
import type { HistoryItem } from '../types'

interface HistoryCardProps {
  readonly item: HistoryItem
  readonly onClick: () => void
}

export function HistoryCard({ item, onClick }: HistoryCardProps) {
  const { t } = useTranslation()
  const hasImage = item.status === 'completed' && item.result_path
  const imagePath = hasImage ? `/api/history/${item.id}/image` : null
  const blobUrl = useAuthImage(imagePath)
  const [imageLoaded, setImageLoaded] = useState(false)

  const statusColor =
    item.status === 'completed'
      ? 'var(--tg-theme-button-color)'
      : item.status === 'failed'
        ? 'var(--tg-theme-destructive-text-color, #ef4444)'
        : 'var(--tg-theme-hint-color)'

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col overflow-hidden rounded-xl text-left transition-all duration-200 active:scale-[0.97]"
      style={{
        border: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 10%, transparent)',
        backgroundColor: 'var(--tg-theme-secondary-bg-color)',
        boxShadow: '0 1px 3px color-mix(in srgb, var(--tg-theme-hint-color) 5%, transparent)',
      }}
    >
      {/* Thumbnail */}
      <div className="relative aspect-square w-full overflow-hidden"
        style={{ backgroundColor: 'color-mix(in srgb, var(--tg-theme-hint-color) 12%, transparent)' }}
      >
        {blobUrl ? (
          <>
            {!imageLoaded && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: 'color-mix(in srgb, var(--tg-theme-hint-color) 12%, transparent)' }}
              >
                <IconSpinner className="h-6 w-6 animate-spin text-tg-hint/50" />
              </div>
            )}
            <img
              src={blobUrl}
              alt={item.positive_prompt}
              className={`h-full w-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
            />
          </>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2">
            {item.status === 'failed' ? (
              <IconX className="h-10 w-10 text-tg-destructive/30" />
            ) : item.status === 'cancelled' ? (
              <IconX className="h-10 w-10 text-tg-hint/20" />
            ) : (
              <IconImage className="h-10 w-10 text-tg-hint/20" />
            )}
          </div>
        )}

        {/* Status badge overlay */}
        <div className="absolute left-2 top-2">
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold backdrop-blur-sm"
            style={{
              backgroundColor: `${statusColor}22`,
              color: statusColor,
            }}
          >
            {t(`history.status.${item.status}`)}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="flex flex-col gap-1 p-2.5">
        <span className="truncate text-xs font-medium text-tg-text">
          {item.positive_prompt.slice(0, 50) || t('history.noPrompt')}
        </span>
        <span className="text-[10px] text-tg-hint">
          {item.width}x{item.height}
        </span>
      </div>
    </button>
  )
}
